import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  Dispatch,
  SetStateAction,
} from "react";
import {
  SpecialistProfileFormState,
  BasicProfileErrors,
  ClinicalProfileErrors,
  AccessErrors,
  HospitalsProceduresErrors,
  ReferralErrors,
  defaultSpecialistProfileFormState,
} from "./SpecialistProfileTypes";

export interface SpecialistProfileContextValue {
  formState: SpecialistProfileFormState;
  setFormState: Dispatch<SetStateAction<SpecialistProfileFormState>>;

  updateBasicProfileField: (
    field: keyof SpecialistProfileFormState["basicProfile"],
    value: string
  ) => void;

  updateClinicalProfileField: (
    field: keyof SpecialistProfileFormState["clinicalProfile"],
    value: string | string[]
  ) => void;

  updateAccessField: (
    field: keyof SpecialistProfileFormState["access"],
    value: string[]
  ) => void;

  updateHospitalsProceduresField: (
    field: keyof SpecialistProfileFormState["hospitalsAndProcedures"],
    value: string | string[] | boolean
  ) => void;

  updateConditionsField: (
    field: keyof SpecialistProfileFormState["conditions"],
    value: string[]
  ) => void;

  updateReferralField: (
    field: keyof SpecialistProfileFormState["referral"],
    value: string | string[]
  ) => void;

  basicProfileErrors: BasicProfileErrors;
  setBasicProfileErrors: Dispatch<SetStateAction<BasicProfileErrors>>;

  clinicalProfileErrors: ClinicalProfileErrors;
  setClinicalProfileErrors: Dispatch<SetStateAction<ClinicalProfileErrors>>;

  accessErrors: AccessErrors;
  setAccessErrors: Dispatch<SetStateAction<AccessErrors>>;

  hospitalsProceduresErrors: HospitalsProceduresErrors;
  setHospitalsProceduresErrors: Dispatch<
    SetStateAction<HospitalsProceduresErrors>
  >;

  referralErrors: ReferralErrors;
  setReferralErrors: Dispatch<SetStateAction<ReferralErrors>>;

  clearAllErrors: () => void;
  resetForm: () => void;
}

const SpecialistProfileContext = createContext<
  SpecialistProfileContextValue | undefined
>(undefined);

interface SpecialistProfileProviderProps {
  children: ReactNode;
  initialState?: Partial<SpecialistProfileFormState>;
}

export const SpecialistProfileProvider: React.FC<
  SpecialistProfileProviderProps
> = ({ children, initialState }) => {
  const [formState, setFormState] = useState<SpecialistProfileFormState>(() => {
    const base = { ...defaultSpecialistProfileFormState };

    if (initialState?.basicProfile) {
      base.basicProfile = { ...base.basicProfile, ...initialState.basicProfile };
    }
    if (initialState?.clinicalProfile) {
      base.clinicalProfile = {
        ...base.clinicalProfile,
        ...initialState.clinicalProfile,
      };
    }
    if (initialState?.access) {
      base.access = { ...base.access, ...initialState.access };
    }
    if (initialState?.hospitalsAndProcedures) {
      base.hospitalsAndProcedures = {
        ...base.hospitalsAndProcedures,
        ...initialState.hospitalsAndProcedures,
      };
    }
    if (initialState?.conditions) {
      base.conditions = { ...base.conditions, ...initialState.conditions };
    }
    if (initialState?.referral) {
      base.referral = { ...base.referral, ...initialState.referral };
    }

    return base;
  });

  const [basicProfileErrors, setBasicProfileErrors] =
    useState<BasicProfileErrors>({});
  const [clinicalProfileErrors, setClinicalProfileErrors] =
    useState<ClinicalProfileErrors>({});
  const [accessErrors, setAccessErrors] = useState<AccessErrors>({});
  const [hospitalsProceduresErrors, setHospitalsProceduresErrors] =
    useState<HospitalsProceduresErrors>({});
  const [referralErrors, setReferralErrors] = useState<ReferralErrors>({});

  const updateBasicProfileField = (
    field: keyof SpecialistProfileFormState["basicProfile"],
    value: string
  ) => {
    setFormState((prev) => ({
      ...prev,
      basicProfile: {
        ...prev.basicProfile,
        [field]: value,
      },
    }));
  };

  const updateClinicalProfileField = (
    field: keyof SpecialistProfileFormState["clinicalProfile"],
    value: string | string[]
  ) => {
    setFormState((prev) => ({
      ...prev,
      clinicalProfile: {
        ...prev.clinicalProfile,
        [field]: value,
      },
    }));
  };

  const updateAccessField = (
    field: keyof SpecialistProfileFormState["access"],
    value: string[]
  ) => {
    setFormState((prev) => ({
      ...prev,
      access: {
        ...prev.access,
        [field]: value,
      },
    }));
  };

  const updateHospitalsProceduresField = (
    field: keyof SpecialistProfileFormState["hospitalsAndProcedures"],
    value: string | string[] | boolean
  ) => {
    setFormState((prev) => ({
      ...prev,
      hospitalsAndProcedures: {
        ...prev.hospitalsAndProcedures,
        [field]: value as any,
      },
    }));
  };

  const updateConditionsField = (
    field: keyof SpecialistProfileFormState["conditions"],
    value: string[]
  ) => {
    setFormState((prev) => ({
      ...prev,
      conditions: {
        ...prev.conditions,
        [field]: value,
      },
    }));
  };

  const updateReferralField = (
    field: keyof SpecialistProfileFormState["referral"],
    value: string | string[]
  ) => {
    setFormState((prev) => ({
      ...prev,
      referral: {
        ...prev.referral,
        [field]: value as any,
      },
    }));
  };

  const clearAllErrors = () => {
    setBasicProfileErrors({});
    setClinicalProfileErrors({});
    setAccessErrors({});
    setHospitalsProceduresErrors({});
    setReferralErrors({});
  };

  const resetForm = () => {
    setFormState(defaultSpecialistProfileFormState);
    clearAllErrors();
  };

  const value: SpecialistProfileContextValue = {
    formState,
    setFormState,
    updateBasicProfileField,
    updateClinicalProfileField,
    updateAccessField,
    updateHospitalsProceduresField,
    updateConditionsField,
    updateReferralField,
    basicProfileErrors,
    setBasicProfileErrors,
    clinicalProfileErrors,
    setClinicalProfileErrors,
    accessErrors,
    setAccessErrors,
    hospitalsProceduresErrors,
    setHospitalsProceduresErrors,
    referralErrors,
    setReferralErrors,
    clearAllErrors,
    resetForm,
  };

  return (
    <SpecialistProfileContext.Provider value={value}>
      {children}
    </SpecialistProfileContext.Provider>
  );
};

export const useSpecialistProfile = (): SpecialistProfileContextValue => {
  const ctx = useContext(SpecialistProfileContext);
  if (!ctx) {
    throw new Error(
      "useSpecialistProfile must be used within a SpecialistProfileProvider"
    );
  }
  return ctx;
};
